import { LEGAL, addressHtml } from "~/config/legal";

const address = addressHtml();

// `body` fields are trusted HTML authored here, rendered into the .ss-prose
// container. They never contain user input.
const en = {
  eyebrow: "Legal",
  lastUpdatedLabel: "Last updated",
  questionsTitle: "Questions about this policy?",
  questionsBody: `Reach us anytime at ${LEGAL.email}.`,

  incompleteWarning:
    "This legal notice is incomplete. The operator still has to supply the details required by §5 DDG.",

  // ── Imprint / Impressum ─────────────────────────────────────────────────
  imprint: {
    metaTitle: "SkillSynergy | Imprint",
    metaDescription:
      "Legal notice and provider identification for SkillSynergy.",
    title: "Imprint",
    sections: [
      {
        h: "Information pursuant to §5 DDG",
        body: `<p>${address}</p>`,
      },
      {
        h: "Contact",
        body: `<p>Phone: <a href="tel:${LEGAL.phone.replace(/\s/g, "")}">${LEGAL.phone}</a><br>Email: <a href="mailto:${LEGAL.email}">${LEGAL.email}</a></p>`,
      },
      {
        h: "VAT identification number",
        body: `<p>VAT ID pursuant to §27a of the German VAT Act (UStG): ${LEGAL.vatId}</p>`,
      },
      {
        h: "Responsible for content pursuant to §18(2) MStV",
        body: `<p>${LEGAL.contentResponsible}<br>${LEGAL.street}, ${LEGAL.postalCode} ${LEGAL.city}</p>`,
      },
      {
        h: "Consumer dispute resolution",
        body: `<p>We are neither obliged nor willing to participate in dispute resolution proceedings before a consumer arbitration board (Verbraucherschlichtungsstelle) within the meaning of §36 VSBG.</p>`,
      },
      {
        h: "Liability for links",
        body: `<p>Our site contains links to external websites over whose content we have no control. We accept no liability for that third-party content. The respective provider or operator of the linked pages is always responsible for their content.</p>`,
      },
    ],
  },

  // ── Privacy policy ───────────────────────────────────────────────────────
  privacy: {
    metaTitle: "SkillSynergy | Privacy Policy",
    metaDescription:
      "How SkillSynergy collects, uses and protects your personal data, and how to exercise your rights under the GDPR.",
    title: "Privacy Policy",
    sections: [
      {
        h: "1. Who is responsible for your data",
        body: `<p>The controller for the processing described here, within the meaning of Art. 4(7) GDPR, is:</p>
<p>${address}<br>Email: <a href="mailto:${LEGAL.email}">${LEGAL.email}</a><br>Phone: ${LEGAL.phone}</p>
<p>We have not appointed a Data Protection Officer, as we are not required to do so under Art. 37 GDPR.</p>`,
      },
      {
        h: "2. What we collect, and why",
        body: `<p>We only process personal data where we have a legal basis under Art. 6(1) GDPR.</p>
<table>
  <thead><tr><th>What</th><th>Why</th><th>Legal basis</th><th>Kept for</th></tr></thead>
  <tbody>
    <tr><td>Server logs: IP address, user agent, requested URL, timestamp</td><td>Delivering the site and defending it against abuse</td><td>Art. 6(1)(f), our legitimate interest in a functioning, secure site</td><td>Held briefly by our hosting provider. We keep no logs of our own.</td></tr>
    <tr><td>Country, derived from your IP address at the edge</td><td>Showing prices in your currency</td><td>Art. 6(1)(f), our legitimate interest in showing relevant prices. The IP address itself is neither stored nor logged by us.</td><td>Not stored</td></tr>
    <tr><td>Contact form: name, email, subject, message</td><td>Answering your enquiry. Your message is stored in our database until we have dealt with it.</td><td>Art. 6(1)(a) consent, and Art. 6(1)(b) where your enquiry concerns a contract</td><td>Deleted automatically after 24 months</td></tr>
    <tr><td>Booking form: name, email, course, track, preferred time</td><td>Arranging your free consultation. Stored in our database until we have arranged it.</td><td>Art. 6(1)(a) consent, and Art. 6(1)(b) steps prior to a contract</td><td>Deleted automatically after 24 months</td></tr>
    <tr><td>A salted hash of your IP address, when you submit a form</td><td>Limiting repeated submissions from the same source (spam protection). The raw IP address is never stored, only a keyed hash of it.</td><td>Art. 6(1)(f), our legitimate interest in preventing abuse of our forms</td><td>Deleted automatically after 24 months</td></tr>
    <tr><td>Campaign parameters (utm_) from the link you arrived through, and the referring site, submitted together with a form</td><td>Knowing which advertisement or link brought an enquiry to us. Nothing is stored on your device for this, and no tracking service is involved.</td><td>Art. 6(1)(f), our legitimate interest in measuring our advertising</td><td>Stored with the enquiry, deleted with it after 24 months</td></tr>
    <tr><td>Your cookie choice</td><td>Remembering what you agreed to</td><td>Art. 6(1)(c), our obligation to honour your choice</td><td>Stored in your browser until you clear it</td></tr>
    <tr><td>Error reports, only if you enable them: IP address, browser, stack trace</td><td>Finding and fixing crashes</td><td>Art. 6(1)(a) consent</td><td>90 days</td></tr>
  </tbody>
</table>
<p>You do not have to provide any of this data. If you do not fill in a form, you simply cannot use that form.</p>`,
      },
      {
        h: "3. Cookies and similar technologies",
        body: `<p>We set no cookies for advertising, tracking or profiling, and we do not use Google Analytics or any comparable product.</p>
<p><b>Strictly necessary</b> storage is set without consent because it exists only to honour a choice you made: a single entry in your browser's local storage recording which categories you agreed to. It is never sent to our servers.</p>
<p><b>Everything else is off until you switch it on.</b> Error reporting loads only after you consent, in line with §25(1) TDDDG. You can change or withdraw your choice at any time via <b>Cookie settings</b> in the footer, with effect for the future.</p>`,
      },
      {
        h: "4. Who else sees your data",
        body: `<p>We do not sell your personal data, and we never will. We share it only with the processors we need to run the service, each under a contract meeting Art. 28 GDPR.</p>
<ul>
  <li><b>Vercel Inc.</b> (United States) hosts the site. Requests are served from EU regions. Transfers rely on Standard Contractual Clauses and Vercel's EU-US Data Privacy Framework certification.</li>
  <li><b>Functional Software, Inc. (Sentry)</b> receives error reports, <i>only if you consent</i>. Data is stored in Sentry's EU region (<code>de.sentry.io</code>).</li>
  <li><b>Neon, Inc. (a Databricks company)</b> provides the database that stores the contact and booking details you submit. The database is hosted in the European Union (Frankfurt), and Neon acts as our processor under a data processing agreement.</li>
</ul>`,
      },
      {
        h: "5. Transfers outside the EU",
        body: `<p>Where a processor is established outside the European Economic Area, or may access data from outside it, we rely on the European Commission's Standard Contractual Clauses under Art. 46(2)(c) GDPR, on an adequacy decision under Art. 45, or on the EU-US Data Privacy Framework. You may request a copy of the relevant safeguards from us at any time.</p>`,
      },
      {
        h: "6. Your rights",
        body: `<p>Under the GDPR you have the right to:</p>
<ul>
  <li>obtain confirmation of whether we process your data, and a copy of it (Art. 15);</li>
  <li>have inaccurate data corrected (Art. 16);</li>
  <li>have your data erased (Art. 17);</li>
  <li>have processing restricted (Art. 18);</li>
  <li>receive your data in a portable, machine-readable format (Art. 20);</li>
  <li><b>object to processing based on our legitimate interests</b> (Art. 21); and</li>
  <li><b>withdraw any consent at any time</b>, without affecting the lawfulness of processing carried out before the withdrawal (Art. 7(3)).</li>
</ul>
<p>To exercise any of these, email <a href="mailto:${LEGAL.email}">${LEGAL.email}</a>. We answer within one month.</p>`,
      },
      {
        h: "7. Complaints",
        body: `<p>If you believe we are handling your data unlawfully, you may lodge a complaint with a supervisory authority, in particular in the Member State of your residence or workplace (Art. 77 GDPR). The authority competent for us is the <a href="${LEGAL.supervisoryAuthority.url}" target="_blank" rel="noopener noreferrer">${LEGAL.supervisoryAuthority.name}</a>.</p>`,
      },
      {
        h: "8. Security",
        body: `<p>The site is served exclusively over TLS. We apply technical and organisational measures appropriate to the risk, as required by Art. 32 GDPR, and we keep the personal data we hold to the minimum the purpose requires.</p>`,
      },
      {
        h: "9. Changes to this policy",
        body: `<p>We update this policy when our processing changes. The current version always carries the date shown at the top of this page.</p>`,
      },
    ],
  },

  // ── Terms ────────────────────────────────────────────────────────────────
  terms: {
    metaTitle: "SkillSynergy | Terms of Service",
    metaDescription:
      "The terms governing enrollment in SkillSynergy courses, including pricing, the statutory right of withdrawal, and acceptable use.",
    title: "Terms of Service",
    sections: [
      {
        h: "1. Scope and provider",
        body: `<p>These terms govern every contract concluded between you and ${LEGAL.entityName}, ${LEGAL.street}, ${LEGAL.postalCode} ${LEGAL.city}, over this website. Conflicting terms of your own do not apply unless we have agreed to them in writing.</p>`,
      },
      {
        h: "2. Courses and enrollment",
        body: `<p>Presenting a course on this site is an invitation to treat, not a binding offer. A contract comes into being when we confirm your enrollment by email. Enrollment grants you access to the live sessions, recordings and materials of the plan you chose. Schedules, formats and content may be adjusted where doing so does not reduce the value of the course.</p>`,
      },
      {
        h: "3. Prices and payment",
        body: `<p>Prices are shown in the currency matching your country and include statutory VAT where applicable. Promotional prices apply only for the period stated. Fees are payable in advance unless agreed otherwise.</p>`,
      },
      {
        h: "4. Right of withdrawal for consumers",
        body: `<p><b>If you are a consumer, you have the right to withdraw from this contract within 14 days without giving any reason.</b> The withdrawal period expires 14 days after the day the contract was concluded.</p>
<p>To exercise it, inform us by an unequivocal statement, for example a letter sent by post or an email to <a href="mailto:${LEGAL.email}">${LEGAL.email}</a>. Sending your communication before the period expires is sufficient.</p>
<p>If you withdraw, we reimburse all payments received from you without undue delay and no later than 14 days from the day we are informed, using the same means of payment you used.</p>
<p>Where you expressly asked us to begin the course during the withdrawal period, you owe us an amount proportionate to what has been supplied up to the point of withdrawal. The right of withdrawal lapses once the course has been fully performed, and you acknowledged on ordering that you would lose it upon full performance.</p>`,
      },
      {
        h: "5. Acceptable use",
        body: `<p>You agree not to share your account, redistribute recordings or materials, or disrupt live sessions. We may suspend accounts that breach these terms or harm other learners, after warning you where circumstances allow.</p>`,
      },
      {
        h: "6. Intellectual property",
        body: `<p>Course materials, code and content remain the property of ${LEGAL.entityName} and its instructors. You receive a personal, non-transferable, non-exclusive licence to use them for your own learning, for as long as your enrollment lasts.</p>`,
      },
      {
        h: "7. Liability",
        body: `<p>We are liable without limitation for intent and gross negligence, and for injury to life, body or health. For slight negligence we are liable only where a material contractual obligation is breached, and then only for the foreseeable damage typical of this kind of contract. Liability under the German Product Liability Act is unaffected.</p>`,
      },
      {
        h: "8. Governing law and jurisdiction",
        body: `<p>German law applies, excluding the UN Convention on Contracts for the International Sale of Goods. If you are a consumer habitually resident in the EU, this choice does not deprive you of the protection of the mandatory provisions of the law of your country of residence.</p>`,
      },
      {
        h: "9. Consumer dispute resolution",
        body: `<p>We are neither obliged nor willing to participate in dispute resolution proceedings before a consumer arbitration board (§36 VSBG).</p>`,
      },
      {
        h: "10. Changes to these terms",
        body: `<p>We may amend these terms where doing so is necessary and reasonable for you. We will notify you at least 30 days before a change takes effect. If you object within that period, we may terminate the contract at the point the change takes effect.</p>`,
      },
    ],
  },
};

export default en;
